import { useState, useCallback } from "react";
import { supabase } from "../supabase/client";
import type { CampaignAnswer } from "../types/CampaignAnswer";

export function useCampaignAnswers(campaignId: string, userId: string | null) {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  const submitAnswers = useCallback(async (answers: { questionId: string; answer: string }[]) => {
    if (!userId) return;

    setSubmitting(true);
    setError(null);
    setSuccess(false);

    try {
      // Delete existing answers for this campaign
      await supabase
        .from("campaign_answers")
        .delete()
        .eq("campaign_id", campaignId)
        .eq("user_id", userId);

      // Insert new answers
      const answersToInsert = answers.map(({ questionId, answer }) => ({
        campaign_id: campaignId,
        question_id: questionId,
        user_id: userId,
        answer
      }));

      const { error: insertError } = await supabase
        .from("campaign_answers")
        .insert(answersToInsert);

      if (insertError) {
        throw insertError;
      }

      setSuccess(true);
    } catch (err) {
      setError(err as Error);
    } finally {
      setSubmitting(false);
    }
  }, [campaignId, userId]);

  const getExistingAnswers = useCallback(async (): Promise<CampaignAnswer[]> => {
    if (!userId) return [];

    const { data, error } = await supabase
      .from("campaign_answers")
      .select("*")
      .eq("campaign_id", campaignId)
      .eq("user_id", userId);

    if (error) {
      console.error("Error fetching existing answers:", error);
      return [];
    }

    return data as CampaignAnswer[];
  }, [campaignId, userId]);

  return {
    submitAnswers,
    getExistingAnswers,
    submitting,
    error,
    success
  };
}
